import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  TextField,
  IconButton,
  Slide,
  Tooltip,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  LockOutlined as LockIcon,
  GavelOutlined as GavelIcon,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import {
  useAccessToken,
  useUpdateOnboardingStep,
  useUserState,
  useToggleFeatureFlag,
} from '@features/user';
import APIService from '../../services/APIService';
import { ProviderBillingInfo } from '../../types/types';
import { useSnackbar } from 'notistack';

// Slide transition for the dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface SigningNoteOnboardingProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

interface StepContent {
  title: string;
  content: React.ReactNode;
  verification?: () => Promise<boolean> | boolean;
  isCompleted?: () => boolean;
}

// List of all US states for the license state dropdown
const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
  'PR',
];

const SigningNoteOnboarding: React.FC<SigningNoteOnboardingProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [signingName, setSigningName] = useState('');
  const [npiNumber, setNPINumber] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [checking, setChecking] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const { getAccessToken } = useAccessToken();
  const updateOnboardingStep = useUpdateOnboardingStep();
  const userState = useUserState();
  const toggleFeatureFlag = useToggleFeatureFlag();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch user settings and provider info on mount
  useEffect(() => {
    if (open) {
      fetchProviderInfo();
    }
  }, [open]);

  const fetchProviderInfo = async () => {
    setChecking(true);
    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIGetRequest({
        requestString: `/user/getProviderBillingInfo`,
        accessToken,
      });

      if (response.ok) {
        const data = response.value.provider_billing_info || {};

        // Initialize fields from provider info if available
        if (data.signing_name) {
          setSigningName(data.signing_name);
        } else if (data.first_name && data.last_name) {
          // Default to full name if signing name isn't set
          setSigningName(`${data.first_name} ${data.last_name}`);
        }

        if (data.npi_number) {
          setNPINumber(data.npi_number);
        }

        if (data.license_number) {
          setLicenseNumber(data.license_number);
        }

        if (data.license_state) {
          setLicenseState(data.license_state);
        }
      }
    } catch (error) {
      console.error('Error fetching provider information:', error);
    } finally {
      setChecking(false);
    }
  };

  const saveSigningInfo = async () => {
    setChecking(true);
    try {
      const accessToken = await getAccessToken();

      // Prepare data with the correct field names
      const updatedFields = {
        signing_name: signingName,
        npi_number: npiNumber,
        license_number: licenseNumber,
        license_state: licenseState,
      };

      const response = await APIService.makeAPIPostRequest({
        requestString: '/user/updateProviderBillingInfo',
        accessToken,
        body: {
          updated_fields: updatedFields,
        },
      });

      if (response.ok) {
        return true;
      } else {
        console.error('Failed to save signing information');
        return false;
      }
    } catch (error) {
      console.error('Error saving signing information:', error);
      return false;
    } finally {
      setChecking(false);
    }
  };

  // Mock function to register onboarding completion
  const registerOnboardingComplete = async () => {
    setChecking(true);
    try {
      // Update onboarding steps to mark this as complete
      updateOnboardingStep('note_signing_onboarding_completed', true);
      return true;
    } catch (error) {
      console.error('Error registering onboarding completion:', error);
      return false;
    } finally {
      setChecking(false);
    }
  };

  // Mock digital signature preview component
  const SignaturePreview = ({ displayName }: { displayName: string }) => {
    return (
      <Box sx={{ my: 2 }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            bgcolor: '#f8f9fa',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            maxWidth: '100%',
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LockIcon sx={{ color: 'success.main', mr: 1 }} />
            <Typography variant="subtitle2">Electronically Signed</Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Typography variant="body2" align="left" sx={{ fontFamily: '"Courier New", monospace' }}>
            {displayName ? displayName : '[Your Name]'}
            <br />
            {licenseNumber ? `License: ${licenseNumber}` : '[License Number]'}
            {licenseState ? ` (${licenseState})` : ''}
            <br />
            {npiNumber ? `NPI: ${npiNumber}` : '[NPI Number]'}
            <br />
            Date: {new Date().toLocaleDateString()}
          </Typography>
        </Paper>
      </Box>
    );
  };

  // Step content definitions
  const steps: StepContent[] = [
    // Welcome step
    {
      title: 'Welcome to Note Signing',
      content: (
        <Box>
          <Typography paragraph>
            This wizard will guide you through setting up your electronic signature for note
            finalization.
          </Typography>
          <Typography paragraph>
            Signed notes are considered legally finalized and will be locked to prevent further
            editing. This meets compliance requirements and provides a clear audit trail of your
            documentation. If needed, you can still amend a signed note after signing.
          </Typography>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Benefits of note signing:
          </Typography>
          <Typography component="ul" sx={{ pl: 2 }}>
            <li>• Meets regulatory and legal requirements</li>
            <li>• Creates a permanent record of your clinical documentation</li>
            <li>• Establishes when a note has been completed and reviewed</li>
            <li>• Prevents unauthorized changes after finalization</li>
          </Typography>
        </Box>
      ),
    },

    // Signature Information step
    {
      title: 'Signature Information',
      content: (
        <Box>
          <Typography paragraph>
            Your signature will appear at the end of notes you sign. Please provide the name you
            want to appear in your signature.
          </Typography>

          <SignaturePreview displayName={signingName} />

          <TextField
            fullWidth
            required
            label="Signature Name"
            value={signingName}
            onChange={e => setSigningName(e.target.value)}
            variant="outlined"
            placeholder="Dr. Jane Smith, MD"
            helperText="This is how your name will appear in the signature block"
            sx={{ my: 2 }}
          />
        </Box>
      ),
      verification: async () => {
        // Verify name is not empty
        if (!signingName.trim()) {
          return false;
        }
        return true;
      },
      isCompleted: () => !!signingName.trim(),
    },

    // License Information step
    {
      title: 'License Information',
      content: (
        <Box>
          <Typography paragraph>
            Please provide your professional credentials that will appear with your signature.
          </Typography>

          <Divider sx={{ mb: 3 }}>
            <Chip label="Optional Fields" size="small" />
          </Divider>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="NPI Number"
              value={npiNumber}
              onChange={e => setNPINumber(e.target.value)}
              variant="outlined"
              placeholder="1234567890"
              helperText="Your National Provider Identifier (NPI)"
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="License Number"
                value={licenseNumber}
                onChange={e => setLicenseNumber(e.target.value)}
                variant="outlined"
                placeholder="MD12345"
                helperText="Your professional license number"
              />

              <FormControl fullWidth>
                <InputLabel id="license-state-label">License State</InputLabel>
                <Select
                  labelId="license-state-label"
                  id="license-state"
                  value={licenseState}
                  label="License State"
                  onChange={e => setLicenseState(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select a state</em>
                  </MenuItem>
                  {US_STATES.map(state => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
            <InfoIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
            Your signature will include this information to help establish your professional
            identity and authority.
          </Typography>
        </Box>
      ),
      verification: async () => {
        // All fields are optional, just save whatever information they've entered
        // TODO: Add ZOD validation
        return true;
      },
      isCompleted: () => true, // This step is always considered completed since all fields are optional
    },

    // Final step
    {
      title: 'Ready to Sign Notes',
      content: (
        <Box>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              You're all set to sign notes!
            </Typography>
          </Box>

          <Typography paragraph>
            You have successfully set up your electronic signature with the following information:
          </Typography>

          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: '4px', mb: 3 }}>
            <Typography variant="subtitle1">
              <strong>Signature:</strong> {signingName}
            </Typography>
            {npiNumber && (
              <Typography variant="body2">
                <strong>NPI:</strong> {npiNumber}
              </Typography>
            )}
            {licenseNumber && licenseState && (
              <Typography variant="body2">
                <strong>License:</strong> {licenseNumber} ({licenseState})
              </Typography>
            )}
          </Paper>

          <Typography paragraph>
            When you're ready to finalize a note, you'll see a "Sign Note" button. Clicking this
            will add your signature and permanently lock the note from further editing.
          </Typography>

          <Box sx={{ bgcolor: '#e8f4fd', p: 2, borderRadius: '4px', mb: 2 }}>
            <Typography variant="body2" color="primary.dark">
              <InfoIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              Once a note is signed, it becomes a permanent record. However, you can still create
              amendments if clinically necessary, which will be tracked as part of the note's
              history.
            </Typography>
          </Box>

          <Box sx={{ bgcolor: '#fef8e6', p: 2, borderRadius: '4px' }}>
            <Typography variant="body2" color="warning.dark">
              <InfoIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              Remember: Make sure your note is complete before signing. Amendments create a version
              history that shows what was changed and when.
            </Typography>
          </Box>
        </Box>
      ),
    },
  ];

  const handleNext = async () => {
    const currentStep = steps[activeStep];

    // If on signature information step, validate name
    if (activeStep === 1) {
      if (!signingName.trim()) {
        return; // Stop here if name is empty
      }
    }

    let passed = true;
    if (currentStep.verification) {
      setChecking(true);
      passed = await currentStep.verification();
      setChecking(false);

      if (!passed) {
        // Show error or handle failed verification
        return;
      }
    }

    // Save signing info after step verification passes (not as part of verification)
    if (activeStep === 2) {
      setChecking(true);
      const saveSuccess = await saveSigningInfo();
      setChecking(false);

      if (!saveSuccess) {
        console.error('Failed to save signing information');
        return;
      }
    }

    // Register onboarding complete when moving to final step
    if (activeStep === 2) {
      setChecking(true);
      const registrationSuccess = await registerOnboardingComplete();
      setChecking(false);

      if (!registrationSuccess) {
        console.error('Failed to register onboarding completion');
      }
    }

    if (activeStep < steps.length - 1) {
      setActiveStep(prevStep => prevStep + 1);
    } else {
      // Call onComplete callback if provided before closing
      if (onComplete) {
        onComplete();
      }
      onClose();
    }
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleDisableNoteSigning = async () => {
    setIsDisabling(true);
    const success = await toggleFeatureFlag('note_signing', false);
    if (success) {
      enqueueSnackbar('Note signing has been disabled.', { variant: 'info' });
      onClose(); // Close the onboarding modal
    } else {
      enqueueSnackbar('Failed to disable note signing. Please try again.', {
        variant: 'error',
      });
    }
    setIsDisabling(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GavelIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Note Signing Setup</Typography>
        </Box>
        <IconButton edge="end" aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, pt: 2 }}>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {steps[activeStep].content}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            mb: userState?.featureFlags?.note_signing ? 2 : 0,
          }}
        >
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || checking}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>

          <Tooltip
            title={
              activeStep === 1 && !signingName.trim()
                ? 'Please enter your signature name to continue'
                : ''
            }
            placement="top"
            arrow
          >
            <Box>
              {' '}
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={checking || (activeStep === 1 && !signingName.trim())}
                endIcon={
                  checking ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : activeStep === steps.length - 1 ? undefined : (
                    <ArrowForwardIcon />
                  )
                }
              >
                {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
              </Button>
            </Box>
          </Tooltip>
        </Box>

        {userState?.featureFlags?.note_signing && (
          <Box
            sx={{
              p: 1,
              bgcolor: theme => theme.palette.grey[100],
              textAlign: 'center',
              borderRadius: 1,
              width: '100%',
            }}
          >
            <Typography variant="caption" sx={{ mr: 0.5 }}>
              Don't want to sign your notes in JotPsych?
            </Typography>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={handleDisableNoteSigning}
              disabled={isDisabling}
              sx={{ textTransform: 'none', fontWeight: 'normal' }}
            >
              {isDisabling ? 'Opting out...' : 'Opt-out here'}
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SigningNoteOnboarding;
